import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { queryCache, QueryKey, useMutation } from "react-query";

import { ApiError } from "fe-shared/src/types/common";
import {
  Button,
  Dialog,
  Loader,
  showNotification,
  TextInput,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";

import {
  DialogHeader,
  InvalidationControls,
} from "fe-shared-transactions/src/components";
import useInvalidationControls from "fe-shared-transactions/src/hooks/useInvalidationControls";

import { InventoryAdjustmentDetails } from "types/inventoryAdjustments";
import { actions } from "../modules/config";
import { ActionConfig, InventoryAdjustmentAction } from "../modules/types";

import * as api from "../modules/api";
import styles from "../InventoryAdjustmentsDetails.module.scss";

const { Invalidate, Approve, SignOf } = InventoryAdjustmentAction;

interface Props {
  inventoryAdjustment: InventoryAdjustmentDetails;
  canInvalidate?: boolean;
  canApproveFirstLevel?: boolean;
  canApproveSecondLevel?: boolean;
  isCurrentUserAssignee: boolean;
  activitiesQueryKey?: QueryKey;
  inventoryAdjustmentQueryKey?: QueryKey;
}

const InventoryAdjustmentActions = ({
  inventoryAdjustment,
  canApproveFirstLevel,
  canInvalidate,
  canApproveSecondLevel,
  isCurrentUserAssignee,
  activitiesQueryKey,
  inventoryAdjustmentQueryKey,
}: Props) => {
  const { t } = useTranslation();
  const { overallAdjustmentNumber, overallAdjustmentId } = inventoryAdjustment;
  const [activeAction, setActiveAction] = useState(actions[0]);
  const [approveComment, setApproveComment] = useState<string>("");
  const { name: activeActionName } = activeAction;

  const {
    isReasonIncorrect,
    invalidationReason,
    isShowToParticipant,
    ...invalidationProps
  } = useInvalidationControls();
  const isInvalidationAction = activeAction.name === Invalidate;
  const isApproveAction = activeAction.name === Approve;

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const visibleActions = useMemo(() => {
    const permissions = {
      [Invalidate]: canInvalidate,
      [Approve]: canApproveFirstLevel,
      [SignOf]: canApproveSecondLevel,
    };

    return actions.filter((action) =>
      action.isVisible(inventoryAdjustment, permissions, isCurrentUserAssignee)
    );
  }, [
    canApproveSecondLevel,
    canApproveFirstLevel,
    canInvalidate,
    inventoryAdjustment,
    isCurrentUserAssignee,
  ]);

  const getMutationConfig = (action: InventoryAdjustmentAction) => ({
    onSuccess: () => {
      queryCache.invalidateQueries(inventoryAdjustmentQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);
      showNotification(
        t(`inventoryAdjustment.actions.${action}.toastMessage`),
        undefined
      );
      closeDialog();
      if (action === InventoryAdjustmentAction.Approve) {
        setApproveComment("");
      }
    },
  });

  const mutations = {
    [Invalidate]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.invalidate({
          overallAdjustmentId,
          invalidationReason,
          isShowToParticipant,
        }),
      getMutationConfig(Invalidate)
    ),
    [Approve]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.approve({ overallAdjustmentId, comment: approveComment }),
      getMutationConfig(Approve)
    ),
    [SignOf]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.approve({ overallAdjustmentId, comment: approveComment }),
      getMutationConfig(Approve)
    ),
  };

  const [applyAction, { isLoading }] = mutations[activeActionName];

  const dialogActions = [
    <Button
      key="1"
      id={`inventory-adjustment-action-dialog-${activeActionName}-button`}
      onClick={() => applyAction()}
      disabled={
        (isInvalidationAction && isReasonIncorrect) ||
        (isApproveAction && !approveComment)
      }
      {...activeAction?.buttonProps}
      {...activeAction?.dialogButtonProps}
    >
      {t(`inventoryAdjustment.actions.${activeActionName}.modalButtonTitle`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={closeDialog}
      id="inventory-adjustment-action-dialog-cancel-button"
    >
      {t("cancel")}
    </Button>,
  ];

  const dialogTitle = (
    <DialogHeader
      type="InventoryAdjustment"
      title={t(`inventoryAdjustment.actions.${activeActionName}.title`)}
      number={overallAdjustmentNumber}
      translationRoot="inventoryAdjustment"
    />
  );

  const applyDialogAction = (action: ActionConfig) => {
    setActiveAction(action);
    openDialog();
  };

  return visibleActions.length ? (
    <>
      <div className={styles.actions}>
        {visibleActions.map((action) => {
          const { name, buttonProps, hasConfirmationDialog } = action;
          const [applyInstantAction] = mutations[name];

          return (
            <Button
              key={name}
              id={`inventory-adjustment-action-${name}-button`}
              className={styles.button}
              disabled={isLoading}
              onClick={() => {
                if (hasConfirmationDialog) {
                  applyDialogAction(action);
                } else {
                  applyInstantAction();
                }
              }}
              {...buttonProps}
            >
              {t(`inventoryAdjustment.actions.${name}.buttonLabel`)}
            </Button>
          );
        })}
        {isDialogRendered && (
          <Dialog
            modal
            title={dialogTitle}
            actions={dialogActions}
            isActive={isDialogActive}
            onClose={closeDialog}
          >
            {isLoading && <Loader isBlocking />}
            {isInvalidationAction && (
              <InvalidationControls
                isWithCheckbox={false}
                invalidationReason={invalidationReason}
                isShowToParticipant={isShowToParticipant}
                {...invalidationProps}
              />
            )}
            {isApproveAction && (
              <TextInput
                required
                isMultiline
                id="approve-dialog-comment-input"
                title={t(
                  "inventoryAdjustment.actions.Approve.commentInputTitle"
                )}
                wrapperClassName={styles.textInput}
                value={approveComment}
                onChange={(e, value) => setApproveComment(value)}
              />
            )}
          </Dialog>
        )}
      </div>
    </>
  ) : null;
};

export default InventoryAdjustmentActions;
